<template>
    <v-wait for="loading garage unit summary">
        <v-row v-if="unitSummary">
            <div class="px-3 mb-5 garage-links body-2">
                <div>
                    Total Garage Vehicles:
                    <router-link
                        :to="{ name: 'units', query: { search: garageName } }"
                        class="accent--text text--lighten-1"
                    >{{ unitSummary.vehicleCount }}
                    </router-link>
                </div>
                <div>
                    Total Garage Containers:
                    <router-link
                        :to="{ name: 'containers', query: { search: garageName } }"
                        class="accent--text text--lighten-1"
                    >{{ unitSummary.containerCount }}
                    </router-link>
                </div>
                <div>
                    Unassigned:
                    <a
                        @click="showUnassigned()"
                        class="accent--text text--lighten-1"
                        v-if="unitSummary.unitsWithoutConfigCount > 0"
                    >{{ unitSummary.unitsWithoutConfigCount }}</a>
                    <span v-else>0</span>
                </div>
                <div>
                    Defects Per Unit:
                    <span>{{ unitSummary.openDefectsPerUnit }}</span>
                </div>
                <div v-if="!!brakeChecks && brakeChecksIncomplete > 0">
                    Brake Checks:
                    <a
                        @click="showBrakeChecks()"
                        class="accent--text text--lighten-1"
                    >{{ brakeChecksIncomplete }}</a>
                    <span class="caption ml-2">{{unitSummary.brakeCheckWeek}}</span>
                </div>
                <div v-else-if="!!unitSummary.brakeCheckWeek">
                    Brake Checks:
                    <span class="caption">{{unitSummary.brakeCheckWeek}}</span>
                </div>
            </div>
        </v-row>

        <v-row v-if="groupedUnits && groupedUnits.length">
            <h2 class="subtitle-2 px-3">VOR Units</h2>
            <v-expansion-panels multiple :value="panelsOpen">
                <v-expansion-panel
                    class="elevation-0 flat"
                    v-for="group in groupedUnits"
                    :key="group.activityName"
                >
                    <v-expansion-panel-header
                        class="pl-3 pr-5 py-1 caption"
                    >{{ group.activityName }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <v-list-item
                            v-for="unit in group.units"
                            :key="unit.vorId"
                            :to="{ name: 'unit', params: { unitId: unit.unitId } }"
                            :class="{itemWarning: unit.startsTomorrow}"
                        >
                            <v-list-item-content>
                                <v-list-item-title>
                                    {{ unit.unitNumber }}
                                    <span
                                        class="caption"
                                    >&nbsp;({{unit.department}})</span>
                                </v-list-item-title>
                                <v-list-item-subtitle
                                    v-if="!unit.startsTomorrow"
                                >{{ unit.vorEndDate | formatDate }}
                                </v-list-item-subtitle>
                                <v-list-item-subtitle v-else>
                                    <strong>Tomorrow</strong>
                                    - {{ unit.vorEndDate | formatDateShort }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </v-row>
        <v-dialog v-model="unassignedUnitDialog" full-width width="450px">
            <v-card v-if="unitSummary && unitSummary.noConfig">
                <v-toolbar dark color="primary">
                    <v-toolbar-title>Unassigned Units</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn @click="unassignedUnitDialog = false" icon text>
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-toolbar>
                <template v-slot:actions>
                    <v-btn @click="unassignedUnitDialog = false">Cancel</v-btn>
                </template>
                <v-list>
                    <v-list-item
                        v-for="unit in unitSummary.noConfig"
                        :key="unit.unitId"
                        :to="{ name: 'unit', params: { unitId: unit.unitId } }"
                    >
                        <v-list-item-content>
                            <v-list-item-title>{{ unit.unitNumber }}</v-list-item-title>
                            <v-list-item-subtitle>{{ unit.unitName }}</v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="unassignedUnitDialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog v-model="brakeCheckDialog" full-width width="500px">
            <v-card>
                <v-toolbar dark color="primary">
                    <v-toolbar-title>Brake Checks</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <v-btn @click="brakeCheckDialog = false" icon text>
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-toolbar>
                <template v-slot:actions>
                    <v-btn @click="brakeCheckDialog = false">Cancel</v-btn>
                </template>
                <v-list v-if="incompleteBrakeChecks.length">
                    <v-subheader>Incomplete</v-subheader>
                    <v-list-item
                        v-for="brakeCheck in incompleteBrakeChecks"
                        :key="brakeCheck.unitId"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                <span>{{ brakeCheck.unitNumber }} ({{brakeCheck.department}})</span>
                            </v-list-item-title>
                            <v-list-item-subtitle>{{ brakeCheck.unitName }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                            <span
                                class="caption"
                            >{{brakeTypes[brakeCheck.brakeCheck.brakeType]}} - {{brakeCheck.brakeCheck.slackType}}</span>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>
                <v-list v-if="completedBrakeChecks.length">
                    <v-subheader>Completed</v-subheader>
                    <v-list-item
                        v-for="brakeCheck in completedBrakeChecks"
                        :key="brakeCheck.unitId"
                        class="completed"
                    >
                        <v-list-item-content>
                            <v-list-item-title>
                                <span>{{ brakeCheck.unitNumber }} ({{brakeCheck.department}})</span>
                            </v-list-item-title>
                            <v-list-item-subtitle>{{ brakeCheck.unitName }}</v-list-item-subtitle>
                        </v-list-item-content>
                        <v-list-item-action>
                            <span
                                class="caption"
                            >{{brakeTypes[brakeCheck.brakeCheck.brakeType]}} - {{brakeCheck.brakeCheck.slackType}}</span>
                        </v-list-item-action>
                    </v-list-item>
                </v-list>
                <v-alert
                    v-if="!isFuture"
                    color="grey"
                    outlined
                    class="mx-3"
                >
                  Brake checks have been completed in principle. Any brake checks not performed by the end of the week have been raised on a scheduled EDN and the documents marked appropriately.
                </v-alert>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn
                        color="primary"
                        @click="confirmCompleteBrakeChecks()"
                        v-if="!isFuture"
                    >Complete
                    </v-btn>
                    <v-btn @click="brakeCheckDialog = false">Close</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-wait>
</template>

<script>
  import GarageLocationsClient from '@/components/GarageLocations/GarageLocationsClient.vue';
  import BrakeCheckClient from '@/components/BrakeChecks/BrakeCheckClient.vue';
  import * as _ from 'lodash';
  import moment from 'moment';
  import {DateFormat} from '../../plugins/date-filter';
  import BrakeCheckHelper from '@/lib/BrakeCheckHelper';

  export default {
    name: 'UnitsSummary',
    mixins: [GarageLocationsClient, BrakeCheckClient],
    props: ['from', 'to', 'period'],
    data() {
      return {
        unassignedUnitDialog: false,
        brakeCheckDialog: false,
        panelsOpen: [],
        unitSummary: null,
        groupedUnits: [],
        brakeChecks: null,
        brakeChecksIncomplete: 0,
        brakeTypes: BrakeCheckHelper.brakeTypes
      };
    },
    async mounted() {
      if (!this.userGarage) {
        return;
      }
      await this.lookup();
    },
    watch: {
      async userGarage(val) {
        await this.lookup();
      },
      async from(val) {
        await this.lookup();
      },
      async to(val) {
        await this.lookup();
      }
    },
    computed: {
      completedBrakeChecks() {
        return this.brakeChecks ? this.brakeChecks.filter(x => x.isComplete) : [];
      },
      incompleteBrakeChecks() {
        return this.brakeChecks ? this.brakeChecks.filter(x => !x.isComplete) : [];
      },
      isFuture() {
        return (
          this.from >
          moment(new Date())
            .add(7, 'days')
            .format(DateFormat.Canonical)
        );
      }
    },
    methods: {
      async lookup() {
        await this.performLookup(this);
      },
      performLookup: _.debounce(async me => {
        me.unitSummary = await me.getUnitSummaryForGaragePeriod(me.userGarage, me.from, me.to);
        me.setOffRoadUnits();

        if (me.period == 'day') {
          me.brakeChecks = await me.getBrakeChecks(me.userGarage, me.from, me.to);
          me.brakeChecksIncomplete = me.brakeChecks.filter(x => !x.isComplete).length;
        }
      }, 10),
      setOffRoadUnits() {
        if (!this.unitSummary) {
          return;
        }
        if (!this.unitSummary.offRoadUnits || this.unitSummary.offRoadUnits.length == 0) {
          this.panelsOpen = [];
          return;
        }
        this.groupedUnits = _.chain(this.unitSummary.offRoadUnits)
          .groupBy('activityName')
          .map((value, key) => ({activityName: key, units: value}))
          .value();
        this.panelsOpen = _.range(this.groupedUnits.length);
      },
      showUnassigned() {
        this.unassignedUnitDialog = true;
      },
      showBrakeChecks() {
        this.brakeCheckDialog = true;
      },
      async confirmCompleteBrakeChecks() {
        const complete = await this.completeBrakeChecks(this.userGarage, this.incompleteBrakeChecks);
        if (!complete) {
          return;
        }

        this.brakeCheckDialog = false;
        this.brakeChecks = await this.getBrakeChecks(this.userGarage, this.from, this.to);
        this.brakeChecksIncomplete = this.brakeChecks.filter(x => !x.isComplete).length;
      }
    }
  };
</script>
<style lang="scss">
    .garage-links a {
        text-decoration: underline;
    }

    .v-expansion-panel.flat,
    .v-expansion-panel.flat:before,
    .theme--light.v-expansion-panels .v-expansion-panel.flat,
    .theme--light.v-expansion-panels .v-expansion-panel.flat:before {
        box-shadow: none;
        background-color: transparent;
    }

    .v-expansion-panel.flat .v-expansion-panel-header {
        min-height: auto;
    }

    .v-expansion-panel--active.flat .v-expansion-panel-header {
        min-height: auto;
    }

    .v-expansion-panel.flat .v-expansion-panel-content__wrap {
        padding-bottom: 0;
    }

    .v-expansion-panel.flat .v-list-item__content {
        padding: 6px 0px;
    }
</style>
